<template>
  <v-container id="my-groups" fluid tag="section">
    <v-overlay :value="isOverlayVisible">
      <v-progress-circular :size="70" :width="7" color="amber" indeterminate class="spinner" />
    </v-overlay>
    <base-v-component heading="Dynamic Groups" sub-heading="" />

    <template>
      <v-toolbar flat color="transparent">
        <v-spacer />

        <v-btn v-if="isAdmin" color="primary" dark class="mb-2 ml-4" @click="_showAddGroupDialog">
          Add Group
        </v-btn>
      </v-toolbar>
    </template>

    <v-data-table :headers="headers" :items="groups" sort-by="name" class="elevation-1">
      <template v-slot:[`item.actions`]="{ item }">
        <div class="senderActions">
          <router-link :to="{ name: 'Dynamic Group Patients', params: { groupId: item.GroupID } }">
            <v-btn color="primary" outlined x-small class="ml-4">
              View Patients
            </v-btn>
            &nbsp;
          </router-link>
          <v-icon v-if="isAdmin" small @click="deleteItem(item)">
            mdi-delete
          </v-icon>
        </div>
      </template>
      <template v-slot:no-data>
        <p class="mb-4 mt-4">
          No groups to display
        </p>
        <v-btn color="primary" outlined class="mb-4" @click="_initialize">
          Refresh
        </v-btn>
      </template>
    </v-data-table>

    <dynamic-group-add-edit-dialog
      :selected-group="groupDialog.selectedGroup"
      :is-visible="groupDialog.isVisible"
      :is-edit-mode="groupDialog.isEditMode"
      :senders="senders"
      :filterTypes="filterTypes"
      @on-submit="onGroupAddEditSubmit"
      @on-close="onGroupAddEditDimiss"
      @on-success="onGroupAddEditSuccess"
      @on-error="onGroupAddEditError"
    />

    <v-snackbar v-model="snackbar">
      {{ notification }}

      <template v-slot:action="{ attrs }">
        <v-btn color="pink" text v-bind="attrs" @click="snackbar = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>

    <div class="py-3" />
  </v-container>
</template>

<script>
  /* eslint-disable */
import { ApiCalls } from "../../../../api-calls";
import UserHelper from "../../../../utils/user-helper";

export default {
  components: {
    DynamicGroupAddEditDialog: () => import("../../components/dynamic-group-add-edit-dialog/dynamic-group-add-edit-dialog.vue")
  },
  data: () => ({
    headers: [
      { text: "Name", value: "Name" },
      { text: "Members", value: "MemberCnt" },
      { text: "Messages", value: "MessageCnt" },
      { text: "Filter Type", value: "filterName" },
      { text: "Actions", value: "actions", sortable: false }
    ],
    snackbar: false,
    notification: "Unknown",
    groups: [],
    senders: [],
    filterTypes: [],
    isOverlayVisible: false,
    groupDialog: {
      isVisible: false,
      isEditMode: false,
      selectedGroup: null
    },
    isAdmin: false
  }),

  computed: {
    formTitle() {
      return this.editedIndex === -1 ? "New Group" : "Edit Group";
    }
  },

  watch: {
    dialog(val) {
      // eslint-disable-next-line no-unused-expressions
      val || this.close();
    },
    '$store.state.posActivePractice': function () {
      this._initialize();
    },
  },

  async created() {
    this.isAdmin = UserHelper.hasAdminAccess();
    await this._initialize();
  },

  methods: {
    async _initialize() {
      await this._loadFilters();
      await this._loadGroups();
      await this._loadSenders();
    },
    async _loadGroups() {
      try {
        if (this.$store.state.posActivePractice) {
          this.isOverlayVisible = true;
          const response = await ApiCalls.fetchDynamicGroups(this.$store.state.posActivePractice.value);
          this.groups = response && response.data && response.data.length ? response.data : [];
          // Add filter name on groups
          const idMappedFilters = {};
          this.filterTypes.forEach(f => {
            idMappedFilters[f.groupFilterTypeID] = f.groupFilterType;
          })
          this.groups = this.groups.map(g => ({ ...g, filterName: `${idMappedFilters[g.GroupFilterTypeID]} ` }))
          // eslint-disable-next-line no-empty
        }
      } catch (error) {
        this.groups = [];
      }
      this.isOverlayVisible = false;
    },
    async _loadSenders() {
      try {
        if (this.$store.state.posActivePractice.value && false) {
          this.isOverlayVisible = true;
          const response = await ApiCalls.fetchSenders(this.$store.state.posActivePractice.value);
          this.senders =
            response && response.data && response.data.length
              ? response.data.map(s => ({ text: s.senderName, value: s.senderID }))
              : [];
        }
      } catch (error) {
        this.senders = [];
      }
      this.isOverlayVisible = false;
    },
    async _loadFilters() {
      try {
        this.isOverlayVisible = true;
        const response = await ApiCalls.fetchDynamicGroupFilters();
        this.filterTypes = response && response.data && response.data.length ? response.data : [];
      } catch (error) {
        this.filterTypes = [];
      }
      this.isOverlayVisible = false;
    },
    updateSender(senderID) {
      this.selectedSenderID = senderID;
    },
    close() {
      this.dialog = false;
      this.$nextTick(() => {
        this.editedItem = {
          id: -1,
          name: "",
          members: "-",
          messages: "-"
        };
        this.editedIndex = -1;
        this.selectedSenderID = null;
        this.isFormDirty = false;
      });
    },
    async save() {
      if (!(this.editedItem.name && this.editedItem.name.length)) {
        this.notification = "Please provide a group name";
        this.snackbar = true;
        return;
      }
      if (!this.selectedSenderID) {
        this.notification = "Please select a sender";
        this.snackbar = true;
        return;
      }
      const activeClinic = UserHelper.getActiveClinic();
      const activePracticeId = activeClinic.PracticeID;
      this.isOverlayVisible = true;
      // New group
      try {
        await ApiCalls.createGroup({
          Name: this.editedItem.name,
          SenderID: this.selectedSenderID,
          PracticeID: activePracticeId
        });
        await this._loadGroups();
      } catch (error) {
        this.notification = "Unable to create group";
        this.snackbar = true;
      }
      this.isOverlayVisible = false;
      this.close();
    },
    async deleteItem(item) {
      if (window.confirm("Are you sure you want to delete this group?")) {
        if (item.groupID) {
          try {
            await ApiCalls.deleteGroup(item.groupID);
            this.notification = "Group has been deleted";
            this.snackbar = true;
            await this._loadGroups();
          } catch (error) {
            this.notification = "Unable to delete the group";
            this.snackbar = true;
          }
        } else {
          this.notification = "Group id not available";
          this.snackbar = true;
        }
      }
    },
    _showAddGroupDialog() {
      this.groupDialog = {
        isVisible: true,
        isEditMode: false,
        selectedGroup: null
      };
    },
    _showEditGroupDialog(group) {
      this.groupDialog = {
        isVisible: true,
        isEditMode: false,
        selectedGroup: group
      };
    },
    _hideGroupDialog() {
      this.groupDialog = {
        isVisible: false,
        isEditMode: false,
        selectedGroup: null
      };
    },
    onGroupAddEditSuccess() {
      this._hideGroupDialog();
      this._loadGroups();
    },
    onGroupAddEditDimiss() {
      this._hideGroupDialog();
      this.isOverlayVisible = false;
    },
    onGroupAddEditSubmit() {
      this.isOverlayVisible = true;
    },
    onGroupAddEditError() {
      this.isOverlayVisible = false;
    }
  }
};
</script>

<style scoped>
@import url("./manage-dynamic-groups-page.scss");
</style>
